import { useState, useCallback } from 'react';

/**
 * Hook to manage filter selections for pixels and events
 * Provides clean state management without complex refs
 */
export const useFilterState = () => {
  const [selectedPixels, setSelectedPixels] = useState<string[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedPixels([]);
    setSelectedEvents([]);
  }, []);

  // Toggle a pixel selection
  const togglePixel = useCallback((pixelId: string) => {
    setSelectedPixels(prev =>
      prev.includes(pixelId) ? prev.filter(id => id !== pixelId) : [...prev, pixelId]
    );
  }, []);

  // Toggle an event selection
  const toggleEvent = useCallback((eventValue: string) => {
    setSelectedEvents(prev =>
      prev.includes(eventValue) ? prev.filter(val => val !== eventValue) : [...prev, eventValue]
    );
  }, []);

  // Remove pixel if no longer used
  const removePixelIfUnused = useCallback((pixelId: string, isStillUsed: boolean) => {
    if (!isStillUsed) {
      setSelectedPixels(prev => prev.filter(id => id !== pixelId));
    }
  }, []);

  // Remove event if no longer used
  const removeEventIfUnused = useCallback((eventValue: string, isStillUsed: boolean) => {
    if (!isStillUsed) {
      setSelectedEvents(prev => prev.filter(val => val !== eventValue));
    }
  }, []);

  return {
    selectedPixels,
    selectedEvents,
    setSelectedPixels,
    setSelectedEvents,
    clearFilters,
    togglePixel,
    toggleEvent,
    removePixelIfUnused,
    removeEventIfUnused,
  };
};
